
import { motion } from "framer-motion";
import { Shield, CheckCircle, FileText, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export default function WarrantyPolicyPage() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 via-green-700 to-green-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Shield className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-6xl font-black mb-6">Warranty Policy</h1>
            <p className="text-2xl text-green-100 max-w-3xl mx-auto">
              1-Year Comprehensive Warranty Protection
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-8"
        >
          {/* Warranty Coverage */}
          <Card className="border-2 border-green-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Warranty Coverage</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Every LCTX Tyre Changer comes with a <strong>1-Year Manufacturer's Warranty</strong> from the date of purchase.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    This warranty covers defects in materials and workmanship under normal use and service.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What's Covered */}
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-8 h-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">What's Covered</h2>
                  <div className="space-y-3">
                    {[
                      "Manufacturing defects in materials",
                      "Workmanship defects in assembly",
                      "Faulty components or parts",
                      "Mechanical failures under normal use",
                      "Structural integrity issues",
                      "Pneumatic system malfunctions"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What's NOT Covered */}
          <Card className="border-2 border-red-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">What's NOT Covered</h2>
                  <div className="space-y-3">
                    {[
                      "Damage from misuse, abuse, or negligence",
                      "Normal wear and tear",
                      "Damage from improper installation",
                      "Use beyond rated capacity or specifications",
                      "Modifications or alterations to the unit",
                      "Damage from accidents or external forces",
                      "Commercial or industrial use (residential warranty only)",
                      "Consumable parts (rubber seals, etc.)"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3 bg-red-50 rounded-lg p-3">
                        <span className="text-red-600 font-bold text-xl flex-shrink-0">✕</span>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* How to Claim */}
          <Card className="border-2 border-purple-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-8 h-8 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">How to Make a Warranty Claim</h2>
                  <div className="space-y-4">
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold text-purple-900 mb-2">Step 1: Contact Us</h3>
                      <p className="text-gray-700">Email us at <strong>hkjenholtd@gmail.com</strong> with your order number and description of the issue.</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold text-purple-900 mb-2">Step 2: Provide Documentation</h3>
                      <p className="text-gray-700">Include photos/videos of the defect and proof of purchase (order confirmation email).</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold text-purple-900 mb-2">Step 3: Assessment</h3>
                      <p className="text-gray-700">Our team will review your claim within 48 hours and determine eligibility.</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold text-purple-900 mb-2">Step 4: Resolution</h3>
                      <p className="text-gray-700">If approved, we'll either repair, replace, or provide a refund at our discretion.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Important Information */}
          <Card className="bg-yellow-50 border-2 border-yellow-300 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-4">📋 Important Information</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>Keep your proof of purchase safe - you'll need it for warranty claims</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>Warranty is non-transferable and valid only for the original purchaser</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>Shipping costs for warranty returns may apply depending on the issue</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>Warranty period begins from the date of delivery, not purchase</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>Register your product at purchase for faster warranty processing</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
